import {
  DatePicker as PolarisDatePicker,
  DatePickerProps as PolarisDatePickerProps,
  Range,
} from '@shopify/polaris';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { StyledElement } from '../types';

const DatePickerWrapper = styled.div`
  .Polaris-DatePicker__Header {
    position: relative;
    top: 0;
  }

  .Polaris-Button:hover {
    background: transparent;
  }
`;

export type DateTimePickerProps = StyledElement &
  Omit<
    PolarisDatePickerProps,
    'month' | 'onMonthChange' | 'selected' | 'year'
  > & {
    selected?: Range;
  };

const getMonthYear = (params: {
  prevMonth?: number;
  prevYear?: number;
  selectedDateRange: Range;
}): { month: number; year: number } => {
  const { prevMonth, prevYear, selectedDateRange } = params;
  const endDate = selectedDateRange.end;
  let month = endDate.getMonth() - 1;

  if (
    prevYear === endDate.getFullYear() &&
    prevMonth &&
    [0, 1].includes(endDate.getMonth() - prevMonth)
  ) {
    month = prevMonth;
  }

  const year = new Date(endDate.getFullYear(), month);

  return {
    month: year.getMonth(),
    year: year.getFullYear(),
  };
};

const DateRangePicker: FunctionComponent<DateTimePickerProps> = ({
  allowRange,
  className,
  onChange,
  selected = {
    end: new Date(),
    start: new Date(),
  },
  ...datePickerProps
}) => {
  const [dateRange, setDateRange] = useState(selected);
  const [{ month, year }, setMonthYear] = useState(
    getMonthYear({
      selectedDateRange: dateRange,
    }),
  );
  useEffect(() => {
    setDateRange(selected);
  }, [selected]);

  useEffect(() => {
    if (dateRange) {
      const { month, year } = getMonthYear({ selectedDateRange: dateRange });
      setMonthYear(
        getMonthYear({
          prevMonth: month,
          prevYear: year,
          selectedDateRange: dateRange,
        }),
      );
    }
  }, [dateRange]);

  const onChangeDate = useCallback(
    (range: Range) => {
      setDateRange(range);
      if (onChange) {
        onChange(range);
      }
    },
    [onChange],
  );

  const onMonthChange = useCallback((month, year) => {
    setMonthYear({ month, year });
  }, []);

  return (
    <DatePickerWrapper className={className}>
      <PolarisDatePicker
        {...datePickerProps}
        allowRange={allowRange}
        onChange={onChangeDate}
        onMonthChange={onMonthChange}
        month={month}
        selected={dateRange}
        year={year}
      />
    </DatePickerWrapper>
  );
};

export default DateRangePicker;
